import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowClockwise, ChartBar, Warning, Bell, PauseCircle, EyeSlash, Clock } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BuyerRejectionsReport } from "@/components/analytics/BuyerRejectionsReport";

/**
 * /admin/affiliate-quality — Rejection-reason breakdown for internal admins.
 *
 * Backed by GET /api/_admin/analytics/rejection-reasons. Shows:
 *  - Headline reject-rate for the selected window.
 *  - Rejection reasons ranked by count (with % of all rejects).
 *  - Per-affiliate table with reject rate + top reason.
 *
 * Access: super_admin or anyone with view_dashboard permission.
 */

const RANGE_PRESETS = [
  { label: "7d",  days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
];

function isoAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

function pct(x) {
  return `${(x * 100).toFixed(1)}%`;
}

function Bar({ value, max }) {
  const width = max > 0 ? Math.max(1, (value / max) * 100) : 0;
  return (
    <div className="w-full h-3 border border-black relative">
      <div className="absolute inset-y-0 left-0 bg-black" style={{ width: `${width}%` }} />
    </div>
  );
}

export default function AffiliateQuality() {
  const { user, hasPerm } = useAuth();
  const allowed =
    user?.role === "super_admin" || (hasPerm && hasPerm("view_dashboard"));

  const [rangeDays, setRangeDays] = useState(30);
  const [affiliates, setAffiliates] = useState([]);
  const [affiliateId, setAffiliateId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [tab, setTab] = useState("intake");

  const load = useCallback(async () => {
    if (!allowed) return;
    setLoading(true);
    try {
      const params = { start: isoAgo(rangeDays) };
      if (affiliateId) params.affiliate_id = affiliateId;
      const { data } = await api.get("/_admin/analytics/rejection-reasons", { params });
      setData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [allowed, rangeDays, affiliateId]);

  useEffect(() => {
    load();
  }, [load]);

  const loadAlerts = useCallback(async () => {
    if (!allowed) return;
    setAlertsLoading(true);
    try {
      const { data } = await api.get("/_admin/analytics/affiliate-alerts", {
        params: { limit: 25 },
      });
      setAlerts(data.alerts || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load alerts");
    } finally {
      setAlertsLoading(false);
    }
  }, [allowed]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  const actOnAlert = async (alertId, action, extra = {}) => {
    try {
      await api.post(`/_admin/analytics/affiliate-alerts/${alertId}/action`, {
        action, ...extra,
      });
      const msg = action === "pause" ? "Affiliate paused"
                : action === "ignore" ? "Alert marked as ignored"
                : "Alert snoozed for 7 days";
      toast.success(msg);
      await loadAlerts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  useEffect(() => {
    // Populate affiliate dropdown on first render.
    api.get("/organizations?type=affiliate")
      .then(({ data }) => setAffiliates(data.organizations || []))
      .catch(() => {});
  }, []);

  const maxReasonCount = useMemo(
    () => (data?.reasons || []).reduce((m, r) => Math.max(m, r.count), 0),
    [data]
  );

  if (!allowed) {
    return (
      <div className="brut-card p-6" data-testid="analytics-forbidden">
        <div className="flex items-center gap-3 text-red-700">
          <Warning size={20} weight="bold" />
          <div className="font-mono uppercase text-xs">
            Access denied — super_admin or view_dashboard required.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="affiliate-quality-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-mono">Analytics</div>
          <h1 className="font-chivo font-black text-4xl md:text-5xl uppercase tracking-tighter leading-none">
            Affiliate quality
          </h1>
          <div className="mt-1 text-sm text-zinc-600">
            Which affiliates are sending clean leads, and why the rejected ones bounce.
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="analytics-refresh"
          className="bg-black text-white border border-black px-3 py-2 text-xs uppercase tracking-widest hover:translate-x-[2px] hover:translate-y-[2px] hover:brut-shadow-sm transition-transform duration-100"
        >
          <ArrowClockwise size={12} className="inline mr-1" /> Refresh
        </button>
      </div>

      <div className="mb-4 flex gap-1" data-testid="affiliate-quality-tabs">
        {[["intake", "Intake rejections"], ["buyer", "Buyer rejections"]].map(([k, label]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`affiliate-quality-tab-${k}`}
                  className={`border-2 border-black px-3 py-1.5 text-xs uppercase tracking-widest transition-colors duration-100 ${tab === k ? "bg-black text-white" : "bg-white hover:bg-zinc-100"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "buyer" ? <BuyerRejectionsReport /> : (<>
      {/* Controls */}
      <div className="brut-card p-4 mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="label-mono">Window</label>
          <div className="flex gap-1 mt-1" data-testid="analytics-range">
            {RANGE_PRESETS.map((r) => (
              <button
                key={r.days}
                onClick={() => setRangeDays(r.days)}
                data-testid={`range-${r.label}`}
                className={`px-3 py-1.5 text-xs uppercase tracking-widest border border-black ${
                  rangeDays === r.days ? "bg-black text-white" : "hover:bg-zinc-100"
                }`}
              >
                Last {r.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="label-mono">Affiliate</label>
          <select
            data-testid="analytics-affiliate"
            className="w-full border border-black px-2 py-1.5 mt-1 font-mono text-xs"
            value={affiliateId}
            onChange={(e) => setAffiliateId(e.target.value)}
          >
            <option value="">All affiliates</option>
            {affiliates.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Headline metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4" data-testid="analytics-metrics">
        <div className="brut-card p-4">
          <div className="label-mono">Processed</div>
          <div className="font-chivo font-black text-4xl mt-1">
            {data?.total_processed ?? "—"}
          </div>
        </div>
        <div className="brut-card p-4 bg-rose-50">
          <div className="label-mono">Rejected</div>
          <div className="font-chivo font-black text-4xl mt-1 text-rose-700">
            {data?.total_rejected ?? "—"}
          </div>
        </div>
        <div className="brut-card p-4">
          <div className="label-mono">Reject rate</div>
          <div className="font-chivo font-black text-4xl mt-1">
            {data ? pct(data.reject_rate) : "—"}
          </div>
        </div>
      </div>

      {/* Reason breakdown */}
      <div className="brut-card p-5 mb-4" data-testid="reasons-panel">
        <div className="flex items-center gap-2 mb-3">
          <ChartBar size={16} weight="bold" />
          <h2 className="font-chivo font-black uppercase tracking-tighter text-xl">
            Rejection reasons
          </h2>
        </div>
        {!data || data.reasons.length === 0 ? (
          <div className="label-mono py-4 text-center">
            {loading ? "Loading…" : "No rejections in this window."}
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="border-b border-black text-[10px] uppercase tracking-widest">
                <th className="text-left py-2 px-3">Reason</th>
                <th className="text-left py-2 px-3">Code</th>
                <th className="text-right py-2 px-3">Count</th>
                <th className="text-right py-2 px-3">%</th>
                <th className="text-left py-2 px-3 w-1/3">Share</th>
              </tr>
            </thead>
            <tbody>
              {data.reasons.map((r) => (
                <tr key={r.code} className="border-b border-zinc-200" data-testid={`reason-${r.code}`}>
                  <td className="py-2 px-3 font-semibold">{r.label}</td>
                  <td className="py-2 px-3 text-[11px] font-mono text-zinc-500">{r.code}</td>
                  <td className="py-2 px-3 text-right font-mono">{r.count}</td>
                  <td className="py-2 px-3 text-right font-mono font-bold">{r.percentage}%</td>
                  <td className="py-2 px-3">
                    <Bar value={r.count} max={maxReasonCount} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Per-affiliate breakdown */}
      {!affiliateId && data && data.by_affiliate && data.by_affiliate.length > 0 && (
        <div className="brut-card p-5 mb-4" data-testid="by-affiliate-panel">
          <h2 className="font-chivo font-black uppercase tracking-tighter text-xl mb-3">
            By affiliate
          </h2>
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="border-b border-black text-[10px] uppercase tracking-widest">
                <th className="text-left py-2 px-3">Affiliate</th>
                <th className="text-right py-2 px-3">Processed</th>
                <th className="text-right py-2 px-3">Rejected</th>
                <th className="text-right py-2 px-3">Reject rate</th>
                <th className="text-left py-2 px-3">Top reason</th>
              </tr>
            </thead>
            <tbody>
              {data.by_affiliate.map((a) => (
                <tr key={a.affiliate_id} className="border-b border-zinc-200" data-testid={`aff-row-${a.affiliate_id}`}>
                  <td className="py-2 px-3 font-semibold">{a.affiliate_name}</td>
                  <td className="py-2 px-3 text-right font-mono">{a.processed}</td>
                  <td className="py-2 px-3 text-right font-mono">{a.rejected}</td>
                  <td className={`py-2 px-3 text-right font-mono font-bold ${
                    a.reject_rate >= 0.4 ? "text-rose-700"
                      : a.reject_rate >= 0.2 ? "text-amber-700" : "text-emerald-700"
                  }`}>{pct(a.reject_rate)}</td>
                  <td className="py-2 px-3 text-[11px]">{a.top_reason_label || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Recent alerts — auto-generated when an affiliate crosses the reject-rate threshold */}
      <div className="brut-card p-5" data-testid="alerts-panel">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Bell size={16} weight="bold" />
            <h2 className="font-chivo font-black uppercase tracking-tighter text-xl">
              Recent alerts
            </h2>
            {alertsLoading && <span className="label-mono">loading…</span>}
          </div>
          <button
            onClick={loadAlerts}
            data-testid="alerts-refresh"
            className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white"
          >
            <ArrowClockwise size={10} className="inline mr-1" /> Refresh
          </button>
        </div>
        {alerts.length === 0 ? (
          <div className="label-mono py-4 text-center">
            {alertsLoading ? "Loading…" : "No affiliate alerts triggered yet — the platform is quiet."}
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead>
              <tr className="border-b border-black text-[10px] uppercase tracking-widest">
                <th className="text-left py-2 px-3">Sent</th>
                <th className="text-left py-2 px-3">Affiliate</th>
                <th className="text-right py-2 px-3">Rate</th>
                <th className="text-right py-2 px-3">Rejected/Processed</th>
                <th className="text-left py-2 px-3">Top reason</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-left py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((a) => {
                const acted = a.action;
                const paused = a.affiliate_status === "paused";
                // Iter-29: stale = open (no action, not paused) AND > 24h since sent.
                const ageMs = a.sent_at ? (Date.now() - Date.parse(a.sent_at)) : 0;
                const stale = !acted && !paused && ageMs > 24 * 3600 * 1000;
                const rowClass = stale
                  ? "border-b border-zinc-200 bg-rose-50 border-l-4 border-l-rose-600"
                  : "border-b border-zinc-200";
                return (
                  <tr key={a.id} className={rowClass} data-testid={`alert-row-${a.id}`}
                      data-stale={stale ? "true" : "false"}>
                    <td className="py-2 px-3 text-[11px] font-mono">
                      {(a.sent_at || "").split("T")[0]}
                      {stale && (
                        <span className="ml-2 text-[9px] bg-rose-600 text-white px-1.5 py-0.5 uppercase tracking-widest font-bold"
                              data-testid={`alert-stale-${a.id}`}>
                          {`${Math.floor(ageMs / 3600000)}h stale`}
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3 font-semibold">{a.affiliate_name}</td>
                    <td className="py-2 px-3 text-right font-mono font-bold text-rose-700">
                      {(a.rate * 100).toFixed(1)}%
                    </td>
                    <td className="py-2 px-3 text-right font-mono">
                      {a.rejected}/{a.processed}
                    </td>
                    <td className="py-2 px-3 text-[11px]">{a.top_reason_label || "—"}</td>
                    <td className="py-2 px-3 text-[10px] uppercase tracking-widest">
                      {paused ? (
                        <span className="text-rose-700 font-bold">Paused</span>
                      ) : acted === "ignore" ? (
                        <span className="text-zinc-500">Ignored</span>
                      ) : acted === "snooze" ? (
                        <span className="text-amber-700">Snoozed → {(a.snoozed_until || "").split("T")[0]}</span>
                      ) : (
                        <span className="text-emerald-800 font-bold">Open</span>
                      )}
                    </td>
                    <td className="py-2 px-3 whitespace-nowrap">
                      {!acted && !paused && (
                        <>
                          <button onClick={() => actOnAlert(a.id, "pause")}
                            data-testid={`alert-pause-${a.id}`}
                            className="border border-rose-600 text-rose-700 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-rose-600 hover:text-white mr-1">
                            <PauseCircle size={10} className="inline" /> Pause
                          </button>
                          <button onClick={() => actOnAlert(a.id, "snooze", { snooze_days: 7 })}
                            data-testid={`alert-snooze-${a.id}`}
                            className="border border-amber-600 text-amber-700 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-amber-600 hover:text-white mr-1">
                            <Clock size={10} className="inline" /> Snooze
                          </button>
                          <button onClick={() => actOnAlert(a.id, "ignore")}
                            data-testid={`alert-ignore-${a.id}`}
                            className="border border-zinc-600 text-zinc-700 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-zinc-600 hover:text-white">
                            <EyeSlash size={10} className="inline" /> Ignore
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>
      </>)}
    </div>
  );
}
